import {HttpErrorResponse} from '@angular/common/http';

/**
 * P22-08: Shared classification of token-refresh failures.
 *
 * Only definitive authentication failures must hard-logout.
 * Network / 5xx failures preserve credentials so the user can retry.
 */
export function isDefinitiveAuthRefreshFailure(error: unknown): boolean {
    if (error == null) {
        return false;
    }

    if (error instanceof Error && !(error as any).status && !(error as any).error) {
        const message = String(error.message || '').toLowerCase();
        // Local precondition failures (no refresh token) are definitive.
        if (message.includes('no refresh token')) {
            return true;
        }
        // Generic Error without HTTP shape is treated as non-definitive
        // unless the message clearly indicates an invalid token.
        return message.includes('token') && (
            message.includes('invalid')
            || message.includes('expired')
            || message.includes('not valid')
        );
    }

    const httpError = error as HttpErrorResponse | any;
    const status = Number(httpError?.status);

    // Network / connectivity — credentials remain valid.
    if (status === 0 || Number.isNaN(status)) {
        return false;
    }

    // Transient server failures — do not invalidate the session.
    if (status >= 500) {
        return false;
    }

    if (status === 401) {
        return true;
    }

    return isAuthSessionInvalidPayload(httpError);
}

/** Detect JWT / session invalid payloads (401 body or 403 token messages). */
export function isAuthSessionInvalidPayload(error: HttpErrorResponse | any): boolean {
    const detail = String(error?.error?.detail || '').toLowerCase();
    const code = String(error?.error?.code || '').toLowerCase();
    const messages = Array.isArray(error?.error?.messages) ? error.error.messages : [];

    if (code === 'token_not_valid') {
        return true;
    }

    if (
        detail.includes('token')
        && (detail.includes('expired') || detail.includes('not valid') || detail.includes('invalid'))
    ) {
        return true;
    }

    return messages.some((message: { message?: string }) => {
        const text = String(message?.message || '').toLowerCase();
        return text.includes('token') && (text.includes('expired') || text.includes('invalid'));
    });
}
